import { LoadingSpinner } from "@shared/components/LoadingSpinner";
import { UserProfileDisplay } from "@shared/components/UserProfileDisplay";
import { useProfile } from "@shared/hooks/useProfile";
import { scrollToTop } from "@shared/utils/scroll";
import theme from "@shared/utils/themeColors";
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCalendar } from "../hooks/useCalendar";
import { EventService } from "../services/eventComments.service";
import { useEventsStore } from "../store/useEventsStore";
import { EventFullDetailsProps } from "../types/calendar.types";
import { getUtcOffsetLabel } from "../utils/timezoneUtils";
import EventCard from "./EventCard";
import EventCommentForm from "./EventCommentForm";
import EventCommentItem from "./EventCommentItem";

const EventFullDetails: React.FC<EventFullDetailsProps> = ({
  event,
  category,
  onClose,
}) => {
  // Global calendar hook (should be at the top)
  const { fetchCalendarData, events, categories, loading } = useCalendar();
  const navigate = useNavigate();
  // Main image (featuredImage)
  const mainImage =
    event.featuredImage && event.featuredImage.length > 0
      ? event.featuredImage[0]
      : undefined;

  // Attachments
  const attachments = event.attachments || [];

  // Helpers to get the main variant URL
  const getMediaUrl = (mediaItem: any) => {
    if (!mediaItem || !mediaItem.variants || mediaItem.variants.length === 0)
      return undefined;
    // Search for 'original', if not found, use the first one
    const original = mediaItem.variants.find((v: any) => v.type === "original");
    return (original || mediaItem.variants[0]).url;
  };

  // ---- Event comments ----
  const { profile } = useProfile();
  const {
    eventComments,
    addComment,
    setEventComments,
    // (you can add fetchEventComments if you have remote loading logic)
  } = useEventsStore();

  // Filter comments for this event
  const comments = eventComments[event.id] || [];

  const [commentContent, setCommentContent] = useState("");
  const [replyTo, setReplyTo] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleComments, setVisibleComments] = useState<
    Record<string, number>
  >({});
  const [isLoadingComments, setIsLoadingComments] = useState(false); // If you have real loading, adjust this

  // Load comments when the component mounts
  useEffect(() => {
    if (!event?.id) return;
    setIsLoadingComments(true);
    EventService.getEventComments(event.id)
      .then((data) => {
        if (setEventComments) setEventComments(event.id, data);
      })
      .catch((err) => {
        console.error("[EventFullDetails] Error fetching comments:", err);
      })
      .finally(() => setIsLoadingComments(false));
  }, [event?.id, setEventComments]);
  const loadingComments = { root: false }; // If you have real loading, adjust this

  // Get available users to mention
  const availableUsers = useMemo(() => {
    const commenters = comments
      .map((comment) => comment.author)
      .filter((author): author is { id: string } => !!author)
      .filter(
        (author, index, self) =>
          index === self.findIndex((a) => a && a.id === author.id)
      );
    // Add the event creator if desired
    if (event.createdByProfile && event.createdByProfile.id) {
      const authorExists = commenters.some(
        (user) => user.id === event.createdByProfile.id
      );
      if (!authorExists) {
        commenters.push(event.createdByProfile);
      }
    }
    return commenters;
  }, [comments, event.createdByProfile]);

  // Send comment
  const handleCommentSubmit = async () => {
    if (!event.id || !commentContent.trim()) return;
    try {
      setIsSubmitting(true);
      const newComment = await addComment(event.id, commentContent, replyTo);
      if (newComment) {
        setCommentContent("");
        setReplyTo(undefined);
        if (newComment.id) {
          setTimeout(() => {
            const commentElement = document.getElementById(
              `event-comment-${newComment.id}`
            );
            if (commentElement) {
              commentElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });
              commentElement.classList.add(theme.bg.accent);
              setTimeout(() => {
                commentElement.classList.remove(theme.bg.accent);
              }, 2000);
            }
          }, 100);
        }
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Mention user
  const handleMention = (username: string) => {
    setCommentContent((prev) => {
      const mentionText = `@${username} `;
      return prev.includes(`@${username}`) ? prev : `${prev}${mentionText}`;
    });
  };

  // Pagination and helpers
  const loadMoreComments = (parentId: string = null) => {
    setVisibleComments((prev) => ({
      ...prev,
      [parentId]: (prev[parentId] || 3) + 3,
    }));
  };
  const getCommentsByParentId = (parentId?: string) => {
    if (parentId === undefined) {
      return comments.filter(
        (comment) => comment.parentId === null || comment.parentId === undefined
      );
    }
    return comments.filter((comment) => comment.parentId === parentId);
  };
  const getLimitedComments = (parentId?: string) => {
    const c = getCommentsByParentId(parentId);
    const limit = visibleComments[parentId || null] || 10;
    return c.slice(0, limit);
  };
  const hasMoreComments = (parentId?: string) => {
    const c = getCommentsByParentId(parentId);
    const limit = visibleComments[parentId || null] || 10;
    return c.length > limit;
  };

  // ---- End comments ----

  return (
    <div className="max-w-5xl mx-auto flex flex-col lg:flex-row lg:space-x-8 xl:space-x-16">
      {/* Content */}
      <div className="flex-1">
        <div className="text-sm font-semibold text-[#0070CC] uppercase mb-2">
          {event.startDate.toDate().toLocaleString(undefined, {
            timeZone: event.timezone,
          })}{" "}
          {event.endDate &&
            `- ${event.endDate.toDate().toLocaleString(undefined, {
              timeZone: event.timezone,
            })}`}
          {event.timezone && (
            <span className="text-xs font-normal ml-1 text-gray-500">
              ({getUtcOffsetLabel(event.timezone)})
            </span>
          )}
        </div>
        {/* Title */}
        <header className="mb-4">
          <h1 className="text-2xl md:text-3xl text-gray-800 dark:text-gray-100 font-bold mb-2">
            {event.title}
          </h1>
        </header>
        {/* Created by */}
        <div className="mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <UserProfileDisplay
                profile={event.createdByProfile}
                label="Created by"
                showAvatar={true}
                avatarSize="sm"
                className="w-full"
              />
            </div>
            <div className="flex items-center gap-2">
              {event.eventType && (
                <span
                  className={`text-xs inline-flex items-center font-medium rounded-full text-center px-2.5 py-1 ${event.eventType === "virtual"
                    ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    : event.eventType === "hybrid"
                      ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                      : "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    }`}
                >
                  {event.eventType === "in-person"
                    ? "In Person"
                    : event.eventType === "virtual"
                      ? "Virtual"
                      : "Hybrid"}
                </span>
              )}
              {category && (
                <div className="text-xs inline-flex items-center font-medium border border-gray-200 dark:border-gray-700/60 text-gray-600 dark:text-gray-400 rounded-full text-center px-2.5 py-1">
                  <span>{category.name}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {/* Location */}
        {event.location && (
          <div className="flex items-center gap-2 mb-4 text-sm text-gray-600 dark:text-gray-400">
            <svg
              className="w-4 h-4 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>{event.location}</span>
          </div>
        )}
        {/* Main photo */}
        {mainImage && (
          <figure className="mb-6">
            <img
              className="w-full rounded-sm"
              src={getMediaUrl(mainImage)}
              alt="Event"
            />
          </figure>
        )}

        {/* Description */}
        <div>
          <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-2">
            Detail Event
          </h2>
          <div
            className="text-base text-gray-700 dark:text-gray-200 mb-4"
            dangerouslySetInnerHTML={{ __html: event.description || "" }}
          />
        </div>
        {/* Photos (attachments) */}
        {attachments.length > 0 && (
          <div>
            <hr className="my-8 border-t border-gray-200 dark:border-gray-700" />
            <h2 className="text-xl leading-snug text-gray-800 dark:text-gray-100 font-bold mb-2">
              Photos ({attachments.length})
            </h2>
            <div className="grid grid-cols-3 gap-4 my-6">
              {attachments.map((media, idx) => {
                const url = getMediaUrl(media);
                if (!url) return null;
                if (media.type === "image") {
                  return (
                    <img
                      key={idx}
                      className="w-full rounded-sm"
                      src={url}
                      alt={`Attachment ${idx + 1}`}
                    />
                  );
                }
                if (media.type === "video") {
                  return (
                    <video key={idx} className="w-full rounded-sm" controls>
                      <source src={url} />
                      Your browser does not support the video tag.
                    </video>
                  );
                }
                return null;
              })}
            </div>
          </div>
        )}
        {/* Comments */}
        <div className="mt-8">
          <hr className="my-8 border-t border-gray-200 dark:border-gray-700" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 font-madefor">
            Comments
          </h3>
          {/* Comment form */}
          <div className="mb-8">
            <EventCommentForm
              profile={profile}
              commentContent={commentContent}
              setCommentContent={setCommentContent}
              onSubmit={handleCommentSubmit}
              isSubmitting={isSubmitting || loadingComments["root"]}
              onCancel={replyTo ? () => setReplyTo(undefined) : undefined}
              availableUsers={availableUsers}
            />
          </div>
          {/* Comments body */}
          <div>
            {isLoadingComments ? (
              <div className="flex justify-center py-4">
                <LoadingSpinner />
              </div>
            ) : getCommentsByParentId(undefined).length > 0 ? (
              <ul className="space-y-5">
                {/* Root level comments with pagination */}
                {getCommentsByParentId(undefined).map((comment) => (
                  <div
                    key={comment.id}
                    id={`event-comment-${comment.id}`}
                    className="transition-colors duration-500"
                  >
                    <EventCommentItem
                      eventId={event.id}
                      comment={comment}
                      onReply={setReplyTo}
                      isReplyActive={replyTo === comment.id}
                      onMention={handleMention}
                      getCommentsByParentId={getCommentsByParentId}
                      getLimitedComments={getLimitedComments}
                      hasMoreComments={hasMoreComments}
                      loadMoreComments={loadMoreComments}
                    />
                  </div>
                ))}
                {/* Load more root comments */}
                {hasMoreComments() && (
                  <div className="text-center mt-5">
                    <button
                      className={`text-sm font-medium ${theme.text.brand} ${theme.text.brandHover}`}
                      onClick={() => loadMoreComments()}
                    >
                      View More Comments
                    </button>
                  </div>
                )}
              </ul>
            ) : (
              <div className="text-center py-4 text-gray-500">
                No comments yet. Be the first to comment!
              </div>
            )}
          </div>
        </div>
        {/* Related Event Section */}
        <div className="mt-12 mb-16">
          <hr className="my-8 border-t border-gray-200 dark:border-gray-700" />
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-4 font-madefor text-lg">
            Related Event
          </h3>
          {(() => {
            // Find related events in the same category, excluding the current one
            const relatedEvents = React.useMemo(() => {
              if (!category) return [];
              return events.filter(
                (e) => e.categoryId === category.id && e.id !== event.id
              );
            }, [events, category, event.id]);
            const relatedEvent =
              relatedEvents.length > 0
                ? relatedEvents[
                Math.floor(Math.random() * relatedEvents.length)
                ]
                : null;
            const relatedCategory = relatedEvent
              ? categories.find((cat) => cat.id === relatedEvent.categoryId)
              : undefined;
            if (loading)
              return (
                <div className="py-4 text-center text-gray-500">
                  Loading related event...
                </div>
              );
            if (!relatedEvent)
              return (
                <div className="py-4 text-center text-gray-400">
                  No related events found.
                </div>
              );
            return (
              <EventCard
                event={relatedEvent}
                category={relatedCategory}
                onClick={() => {
                  scrollToTop("instant");
                  navigate(`/calendar/event/${relatedEvent.id}`);
                }}
                userId={profile?.id || ""}
                fetchCalendarData={fetchCalendarData}
              />
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default EventFullDetails;
